/**
 * Configuration Context
 * Makes config available throughout the app
 */

import React, { createContext, useContext, ReactNode } from 'react';
import { BackendConfig, DEFAULT_BACKEND_CONFIG } from '../types/config';

interface ConfigContextType {
  config: BackendConfig | null;
  isLoading: boolean;
  error: string | null;
}

const ConfigContext = createContext<ConfigContextType>({
  config: null,
  isLoading: true,
  error: null,
});

export function useConfig(): BackendConfig {
  const context = useContext(ConfigContext);

  if (!context.config) {
    return DEFAULT_BACKEND_CONFIG;
  }

  return context.config;
}

export function useConfigLoading(): { isLoading: boolean; error: string | null } {
  const context = useContext(ConfigContext);
  return {
    isLoading: context.isLoading,
    error: context.error,
  };
}

interface ConfigProviderProps {
  children: ReactNode;
  config: BackendConfig | null;
  isLoading: boolean;
  error: string | null;
}

export function ConfigProvider({
  children,
  config,
  isLoading,
  error,
}: ConfigProviderProps) {
  return (
    <ConfigContext.Provider value={{ config, isLoading, error }}>
      {children}
    </ConfigContext.Provider>
  );
}
